import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useSettingsStore } from './settings'
import { getFontCatalog, registerCatalogFamily } from '../services/fontCatalog'
import { readInstalledFamilies, readOfflinePinnedFamilies, writeInstalledFamily } from '../services/fontCatalogStorage'
import type { FontCatalogFamily } from '../types/fontCatalog'
import { APP_FONTS, APP_FONT_CAPABILITIES, type FontCapabilities } from '../constants/fonts'

const FALLBACK_FONTS = [
  'Georgia',
  'Garamond',
  'Times New Roman',
  'Palatino',
  'Inter',
  'Roboto',
  'Helvetica Neue',
  'Arial',
  'Merriweather',
  'Lora',
  'Playfair Display',
  'Fira Code',
  'JetBrains Mono',
  'Monospace',
]

function sortFamilies(families: FontCatalogFamily[]): FontCatalogFamily[] {
  return [...families].sort((a, b) => a.family.localeCompare(b.family))
}

export const useFontsStore = defineStore('fonts', () => {
  const settings = useSettingsStore()
  const systemFonts = ref<string[]>([])
  const systemFontCapabilities = ref<Record<string, FontCapabilities>>({})
  const loaded = ref(false)
  const loading = ref(false)
  const catalogFamilies = ref<FontCatalogFamily[]>([])
  const installedCatalogFamilies = ref<FontCatalogFamily[]>([])
  const catalogLoading = ref(false)
  const catalogLoaded = ref(false)
  const catalogError = ref<string | null>(null)
  const downloadErrors = ref<Record<string, string>>({})
  const downloadingIds = ref<Set<string>>(new Set())
  const readyCatalogIds = ref<Set<string>>(new Set())

  let inFlightPromise: Promise<string[]> | null = null
  let catalogPromise: Promise<FontCatalogFamily[]> | null = null
  let initializationPromise: Promise<void> | null = null
  let settingsWatcherInstalled = false

  const installedCatalogFontNames = computed(() =>
    installedCatalogFamilies.value
      .filter((family) => readyCatalogIds.value.has(family.id))
      .map((family) => family.family),
  )

  const allAppFontNames = computed(() => {
    const seen = new Set<string>()
    return [...APP_FONTS, ...installedCatalogFontNames.value].filter((font) => {
      const key = font.toLocaleLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  })

  const pickerAppFontNames = computed(() => {
    const availableByName = new Map(
      allAppFontNames.value.map((font) => [font.toLocaleLowerCase(), font]),
    )
    const hidden = new Set(settings.hiddenAppFonts.map((font) => font.toLocaleLowerCase()))
    const result: string[] = []
    const added = new Set<string>()

    for (const preferredName of settings.appFontOrder) {
      const key = preferredName.toLocaleLowerCase()
      const availableName = availableByName.get(key)
      if (availableName && !hidden.has(key) && !added.has(key)) {
        result.push(availableName)
        added.add(key)
      }
    }

    for (const font of allAppFontNames.value) {
      const key = font.toLocaleLowerCase()
      if (!hidden.has(key) && !added.has(key)) {
        result.push(font)
        added.add(key)
      }
    }

    return result
  })

  function isAppFontVisible(font: string): boolean {
    const normalized = font.toLocaleLowerCase()
    return pickerAppFontNames.value.some((name) => name.toLocaleLowerCase() === normalized)
  }

  function setAppFontOrder(fonts: readonly string[]): void {
    const availableByName = new Map(
      allAppFontNames.value.map((font) => [font.toLocaleLowerCase(), font]),
    )
    const ordered: string[] = []
    const seen = new Set<string>()
    for (const font of fonts) {
      const key = font.toLocaleLowerCase()
      const availableName = availableByName.get(key)
      if (availableName && !seen.has(key)) {
        ordered.push(availableName)
        seen.add(key)
      }
    }
    settings.appFontOrder = ordered
  }

  function hideAppFont(font: string): void {
    const normalized = font.toLocaleLowerCase()
    if (!settings.hiddenAppFonts.some((name) => name.toLocaleLowerCase() === normalized)) {
      settings.hiddenAppFonts = [...settings.hiddenAppFonts, font]
    }
    settings.appFontOrder = settings.appFontOrder.filter(
      (name) => name.toLocaleLowerCase() !== normalized,
    )
  }

  function showAppFont(font: string): void {
    const normalized = font.toLocaleLowerCase()
    settings.hiddenAppFonts = settings.hiddenAppFonts.filter(
      (name) => name.toLocaleLowerCase() !== normalized,
    )
    if (!settings.appFontOrder.some((name) => name.toLocaleLowerCase() === normalized)) {
      settings.appFontOrder = [
        ...pickerAppFontNames.value.filter((name) => name.toLocaleLowerCase() !== normalized),
        font,
      ]
    }
  }

  async function loadFonts(): Promise<string[]> {
    if (loaded.value) return systemFonts.value
    if (loading.value && inFlightPromise) return inFlightPromise

    loading.value = true
    inFlightPromise = (async () => {
      try {
        const result = await window.api?.listFonts()
        if (Array.isArray(result) && result.length > 0) {
          systemFonts.value = result.map((font) => font.family)
          systemFontCapabilities.value = Object.fromEntries(
            result.map((font) => [font.family.toLocaleLowerCase(), {
              bold: font.bold,
              italic: font.italic,
              weights: font.weights,
              variableWeightRange: font.variableWeightRange,
            }]),
          )
        } else if (systemFonts.value.length === 0) {
          systemFonts.value = FALLBACK_FONTS
        }
      } catch (err) {
        console.warn('[FontsStore] Failed to load system fonts:', err)
        if (systemFonts.value.length === 0) systemFonts.value = FALLBACK_FONTS
      } finally {
        loaded.value = true
        loading.value = false
        inFlightPromise = null
      }
      return systemFonts.value
    })()

    return inFlightPromise
  }

  async function rehydrateStoredFamilies(ids: readonly string[]): Promise<void> {
    const pinned = await readOfflinePinnedFamilies()
    const stored = sortFamilies(await readInstalledFamilies([...new Set([...ids, ...pinned.map(family => family.id)])]))
    installedCatalogFamilies.value = stored

    await Promise.all(stored.map(async (family) => {
      try {
        await registerCatalogFamily(family, { fetchMissingCore: true })
        readyCatalogIds.value = new Set(readyCatalogIds.value).add(family.id)
        const { [family.id]: _cleared, ...remaining } = downloadErrors.value
        downloadErrors.value = remaining
      } catch (error) {
        const nextReady = new Set(readyCatalogIds.value)
        nextReady.delete(family.id)
        readyCatalogIds.value = nextReady
        downloadErrors.value = {
          ...downloadErrors.value,
          [family.id]: error instanceof Error ? error.message : String(error),
        }
        console.warn(`[FontsStore] Failed to rehydrate ${family.family}:`, error)
      }
    }))
  }

  async function initializeCatalogFonts(): Promise<void> {
    if (initializationPromise) return initializationPromise
    initializationPromise = (async () => {
      await rehydrateStoredFamilies(settings.installedFontIds)

      if (!settingsWatcherInstalled) {
        settingsWatcherInstalled = true
        watch(
          () => [...settings.installedFontIds],
          (ids) => { void rehydrateStoredFamilies(ids) },
        )
      }
    })()
    return initializationPromise
  }

  async function installCatalogFamily(
    family: FontCatalogFamily,
    options: { updateSyncedIds?: boolean; makeVisible?: boolean } = {},
  ): Promise<void> {
    if (downloadingIds.value.has(family.id)) return
    downloadingIds.value = new Set(downloadingIds.value).add(family.id)
    const { [family.id]: _cleared, ...remainingErrors } = downloadErrors.value
    downloadErrors.value = remainingErrors

    try {
      await registerCatalogFamily(family, { fetchMissingCore: true })
      await writeInstalledFamily(family)
      readyCatalogIds.value = new Set(readyCatalogIds.value).add(family.id)
      installedCatalogFamilies.value = sortFamilies([
        ...installedCatalogFamilies.value.filter((item) => item.id !== family.id),
        family,
      ])

      if (options.updateSyncedIds !== false) {
      if (options.makeVisible !== false) showAppFont(family.family)
        if (!settings.installedFontIds.includes(family.id)) {
          settings.installedFontIds = [...settings.installedFontIds, family.id].sort()
        }
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      downloadErrors.value = { ...downloadErrors.value, [family.id]: message }
      throw error
    } finally {
      const next = new Set(downloadingIds.value)
      next.delete(family.id)
      downloadingIds.value = next
    }
  }

  function reconcileSyncedFamilies(families: FontCatalogFamily[]): void {
    const desired = new Set(settings.installedFontIds)

    // A newly synced device has only IDs. Once the user opens the catalog and
    // its manifest is available, independently fetch that device's core files.
    for (const family of families) {
      if (desired.has(family.id) && !readyCatalogIds.value.has(family.id)) {
        void installCatalogFamily(family, { updateSyncedIds: false, makeVisible: false }).catch((error) => {
          console.warn(`[FontsStore] Failed to install synced font ${family.family}:`, error)
        })
      }
    }
  }

  async function loadCatalog(): Promise<FontCatalogFamily[]> {
    if (catalogLoaded.value) return catalogFamilies.value
    if (catalogPromise) return catalogPromise

    catalogLoading.value = true
    catalogError.value = null
    catalogPromise = (async () => {
      try {
        // TODO(font-catalog pipeline): this first-pass list only needs family,
        // id, category, license and subsets. Publish a lightweight index plus
        // per-family detail files upstream to avoid transferring full `files`
        // arrays for discovery.
        // TODO(previews): either register a non-persisted core face for an
        // ephemeral preview, or consume pipeline-generated specimen images.
        const result = await getFontCatalog()
        catalogFamilies.value = sortFamilies(result.manifest.fonts)
        catalogLoaded.value = true
        reconcileSyncedFamilies(catalogFamilies.value)

        if (result.refresh) {
          void result.refresh.then((fresh) => {
            catalogFamilies.value = sortFamilies(fresh.fonts)
            reconcileSyncedFamilies(catalogFamilies.value)
          }).catch((error) => {
            // The cached catalog remains usable offline.
            console.warn('[FontsStore] Font catalog refresh failed:', error)
          })
        }
        return catalogFamilies.value
      } catch (error) {
        catalogError.value = error instanceof Error ? error.message : String(error)
        throw error
      } finally {
        catalogLoading.value = false
        catalogPromise = null
      }
    })()
    return catalogPromise
  }

  function isCatalogFontInstalled(id: string): boolean {
    return readyCatalogIds.value.has(id)
  }

  function findCatalogFamilyByName(name: string): FontCatalogFamily | undefined {
    const normalized = name.replace(/['"]/g, '').split(',')[0].trim().toLocaleLowerCase()
    return catalogFamilies.value.find((family) => family.family.toLocaleLowerCase() === normalized)
  }

  function getFontCapabilities(name: string): FontCapabilities | null {
    const normalized = name.replace(/['"]/g, '').split(',')[0].trim().toLocaleLowerCase()
    if (!normalized) return null

    const catalogFamily = installedCatalogFamilies.value.find(
      (family) => family.family.toLocaleLowerCase() === normalized,
    )
    if (catalogFamily) {
      const staticWeights = new Set<number>()
      const ranges: Array<{ min: number; max: number }> = []
      for (const file of catalogFamily.files) {
        const weights = file.weight.trim().split(/\s+/).map(Number).filter(Number.isFinite)
        if (weights.length === 2) ranges.push({ min: weights[0], max: weights[1] })
        else if (weights.length === 1) staticWeights.add(weights[0])
      }
      const variableWeightRange = ranges.length
        ? {
            min: Math.min(...ranges.map((range) => range.min)),
            max: Math.max(...ranges.map((range) => range.max)),
          }
        : null
      return {
        bold: (variableWeightRange?.max ?? 0) >= 600 || [...staticWeights].some((weight) => weight >= 600),
        italic: catalogFamily.files.some((file) => file.style === 'italic'),
        weights: [...staticWeights].sort((a, b) => a - b),
        variableWeightRange,
      }
    }

    const appEntry = Object.entries(APP_FONT_CAPABILITIES).find(
      ([family]) => family.toLocaleLowerCase() === normalized,
    )
    return appEntry?.[1] ?? systemFontCapabilities.value[normalized] ?? null
  }

  return {
    systemFonts,
    systemFontCapabilities,
    loaded,
    loading,
    catalogFamilies,
    installedCatalogFamilies,
    installedCatalogFontNames,
    allAppFontNames,
    pickerAppFontNames,
    catalogLoading,
    catalogLoaded,
    catalogError,
    downloadErrors,
    downloadingIds,
    readyCatalogIds,
    loadFonts,
    loadCatalog,
    initializeCatalogFonts,
    installCatalogFamily,
    isCatalogFontInstalled,
    isAppFontVisible,
    setAppFontOrder,
    hideAppFont,
    showAppFont,
    findCatalogFamilyByName,
    getFontCapabilities,
  }
})
